import React, { useCallback, useEffect, useMemo, useState } from "react";
import rohanApiClient from "./rohanApiClient";
import "./AdminUserAnalytics.css";

const API_URL = "https://rohanbackend.axiomos.in/user/analytics";

const formatDateTime = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const formatMonthLabel = (item) => {
  const year = item?._id?.year;
  const month = item?._id?.month;

  if (!year || !month) return "—";

  const date = new Date(year, month - 1, 1);

  return date.toLocaleString("en-IN", {
    month: "short",
    year: "numeric",
  });
};

const getMaxValue = (arr, key) => {
  if (!Array.isArray(arr) || !arr.length) return 0;

  return Math.max(...arr.map((item) => Number(item?.[key]) || 0), 0);
};

const getUserName = (user) =>
  user?.name ||
  user?.fullName ||
  user?.userName ||
  user?.employeeName ||
  "Unnamed User";

const getUserEmail = (user) =>
  user?.emailId || user?.email || user?.officialEmail || "—";

const getUserPhone = (user) =>
  user?.phone || user?.phoneNumber || user?.mobile || user?.mobileNumber || "—";

const getRoleName = (user) => {
  const role = user?.role;

  if (!role) return user?.roleName || "";

  if (typeof role === "string") return role;

  if (typeof role === "object") {
    return (
      role.roleName || role.name || role.title || role.label || role.value || ""
    );
  }

  return String(role);
};

const isAdminUser = (user) => {
  const normalizedRole = getRoleName(user)
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  return normalizedRole.includes("admin");
};

const getUserRole = (user) => getRoleName(user) || "—";

// const getUserRole = (user) => {
//   const role = user?.role;

//   let roleName = "";

//   if (!role) {
//     roleName = user?.roleName || "";
//   } else if (typeof role === "string") {
//     roleName = role;
//   } else if (typeof role === "object") {
//     roleName =
//       role.roleName ||
//       role.name ||
//       role.title ||
//       role.label ||
//       role.value ||
//       "";
//   }

//   const normalizedRole = String(roleName).trim().toLowerCase();

//   if (
//     normalizedRole === "admin" ||
//     normalizedRole === "administrator" ||
//     normalizedRole === "super admin" ||
//     normalizedRole === "superadmin"
//   ) {
//     return "—";
//   }

//   return roleName || "—";
// };

const getUserDesignation = (user) =>
  user?.designation || user?.position || user?.department || "—";

const StatCard = ({ label, value }) => (
  <div className="aua-stat-card">
    <div className="aua-stat-value">{value ?? 0}</div>
    <div className="aua-stat-label">{label}</div>
  </div>
);

const BarList = ({
  title,
  data = [],
  valueKey = "count",
  labelKey = "_id",
  emptyText = "No data found",
}) => {
  const max = getMaxValue(data, valueKey);

  return (
    <div className="aua-panel">
      <div className="aua-panel-header">
        <h3>{title}</h3>
      </div>

      {!data.length ? (
        <div className="aua-empty">{emptyText}</div>
      ) : (
        <div className="aua-bar-list">
          {data.map((item, index) => {
            const value = Number(item?.[valueKey]) || 0;
            const rawLabel = item?.[labelKey];

            const label =
              typeof rawLabel === "object" && rawLabel !== null
                ? rawLabel.roleName ||
                  rawLabel.name ||
                  rawLabel.projectName ||
                  rawLabel.title ||
                  rawLabel.label ||
                  rawLabel.value ||
                  "Unknown"
                : rawLabel || "Unknown";

            const width = max ? `${(value / max) * 100}%` : "0%";

            return (
              <div className="aua-bar-row" key={`${label}-${index}`}>
                <div className="aua-bar-row-top">
                  <span className="aua-bar-label">{label}</span>
                  <span className="aua-bar-value">{value}</span>
                </div>

                <div className="aua-bar-track">
                  <div className="aua-bar-fill" style={{ width }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SimpleTable = ({
  title,
  columns = [],
  rows = [],
  emptyText = "No records found",
}) => (
  <div className="aua-panel">
    <div className="aua-panel-header">
      <h3>{title}</h3>
    </div>

    {!rows.length ? (
      <div className="aua-empty">{emptyText}</div>
    ) : (
      <div className="aua-table-wrap">
        <table className="aua-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={row._id || row.name || row.emailId || rowIndex}>
                {columns.map((col) => (
                  <td key={col.key}>
                    {col.render ? col.render(row) : (row[col.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const GrowthChart = ({ data = [] }) => {
  const max = useMemo(() => getMaxValue(data, "count"), [data]);

  return (
    <div className="aua-panel">
      <div className="aua-panel-header">
        <h3>User Growth</h3>
      </div>

      {!data.length ? (
        <div className="aua-empty">No growth data found</div>
      ) : (
        <div className="aua-growth-chart">
          {data.map((item, index) => {
            const count = Number(item?.count) || 0;
            const height = max ? `${Math.max((count / max) * 100, 6)}%` : "6%";

            return (
              <div
                className="aua-growth-item"
                key={`${item?._id?.year}-${item?._id?.month}-${index}`}
              >
                <div className="aua-growth-bar-wrap">
                  <div className="aua-growth-count">{count}</div>
                  <div className="aua-growth-bar" style={{ height }} />
                </div>

                <div className="aua-growth-label">{formatMonthLabel(item)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ProjectUsersPanel = ({ projectUsers = [] }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTab, setActiveTab] = useState("top");

  const openProjectPopup = (project) => {
    setSelectedProject(project);
    setActiveTab("top");
  };

  const closeProjectPopup = () => {
    setSelectedProject(null);
    setActiveTab("top");
  };

  return (
    <>
      <div className="aua-panel aua-project-users-panel">
        <div className="aua-panel-header aua-panel-header-split">
          <div>
            <h3>Project-wise User Activity</h3>
            <p>
              View active, inactive, and top users project-wise without showing
              the full user list on the page.
            </p>
          </div>
        </div>

        {!projectUsers.length ? (
          <div className="aua-empty">
            No project-wise users found. Backend should return projectUsers with
            users inside each project.
          </div>
        ) : (
          <div className="aua-project-summary-grid">
            {projectUsers.map((project) => {
              const users = Array.isArray(project.users) ? project.users : [];

              const activeUsers = users.filter(
                (user) => user?.isActive !== false,
              );

              const inactiveUsers = users.filter(
                (user) => user?.isActive === false,
              );

              const topUsers = [...users]
                .sort(
                  (a, b) =>
                    Number(b?.loginCount || b?.count || 0) -
                    Number(a?.loginCount || a?.count || 0),
                )
                .slice(0, 5);

              return (
                <div
                  className="aua-project-summary-card"
                  key={project.projectId}
                >
                  <div className="aua-project-summary-head">
                    <div>
                      <h4>{project.projectName}</h4>
                      <p>
                        {project.totalUsers || users.length || 0} total users
                      </p>
                    </div>

                    <button
                      type="button"
                      className="aua-project-view-btn"
                      onClick={() => openProjectPopup(project)}
                    >
                      View Users
                    </button>
                  </div>

                  <div className="aua-project-metrics">
                    <div className="aua-project-metric">
                      <span className="aua-project-metric-value active">
                        {project.activeUsers ?? activeUsers.length}
                      </span>
                      <span className="aua-project-metric-label">Active</span>
                    </div>

                    <div className="aua-project-metric">
                      <span className="aua-project-metric-value inactive">
                        {project.inactiveUsers ?? inactiveUsers.length}
                      </span>
                      <span className="aua-project-metric-label">Inactive</span>
                    </div>

                    <div className="aua-project-metric">
                      <span className="aua-project-metric-value top">
                        {topUsers.length}
                      </span>
                      <span className="aua-project-metric-label">
                        Top Users
                      </span>
                    </div>
                  </div>

                  <div className="aua-project-mini-list">
                    {topUsers.length ? (
                      topUsers.slice(0, 3).map((user, index) => (
                        <div
                          className="aua-project-mini-user"
                          key={user?._id || user?.id || user?.emailId || index}
                        >
                          <span>{getUserName(user)}</span>
                          <strong>
                            {user?.loginCount ?? user?.count ?? 0}
                          </strong>
                        </div>
                      ))
                    ) : (
                      <div className="aua-project-mini-empty">
                        No top users found
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedProject ? (
        <ProjectUsersPopup
          project={selectedProject}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onClose={closeProjectPopup}
        />
      ) : null}
    </>
  );
};

const ProjectUsersPopup = ({ project, activeTab, setActiveTab, onClose }) => {
  const users = Array.isArray(project?.users) ? project.users : [];

  const activeUsers = useMemo(
    () => users.filter((user) => user?.isActive !== false),
    [users],
  );

  const inactiveUsers = useMemo(
    () => users.filter((user) => user?.isActive === false),
    [users],
  );

  const topUsers = useMemo(() => {
    return [...users]
      .sort(
        (a, b) =>
          Number(b?.loginCount || b?.count || 0) -
          Number(a?.loginCount || a?.count || 0),
      )
      .slice(0, 10);
  }, [users]);

  const visibleUsers = useMemo(() => {
    if (activeTab === "active") return activeUsers;
    if (activeTab === "inactive") return inactiveUsers;
    return topUsers;
  }, [activeTab, activeUsers, inactiveUsers, topUsers]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="aua-project-popup"
      role="dialog"
      aria-modal="true"
      aria-label={`${project?.projectName} users`}
      onMouseDown={onClose}
    >
      <div
        className="aua-project-popup-box"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="aua-project-popup-header">
          <div>
            <h3>{project?.projectName || "Project Users"}</h3>
            <p>
              {users.length} total · {activeUsers.length} active ·{" "}
              {inactiveUsers.length} inactive
            </p>
          </div>

          <button
            type="button"
            className="aua-project-popup-close"
            onClick={onClose}
            aria-label="Close project users popup"
          >
            ×
          </button>
        </div>

        <div className="aua-project-popup-tabs">
          <button
            type="button"
            className={activeTab === "top" ? "active" : ""}
            onClick={() => setActiveTab("top")}
          >
            Top Users
            <span>{topUsers.length}</span>
          </button>

          <button
            type="button"
            className={activeTab === "active" ? "active" : ""}
            onClick={() => setActiveTab("active")}
          >
            Active
            <span>{activeUsers.length}</span>
          </button>

          <button
            type="button"
            className={activeTab === "inactive" ? "active" : ""}
            onClick={() => setActiveTab("inactive")}
          >
            Inactive
            <span>{inactiveUsers.length}</span>
          </button>
        </div>

        <div className="aua-project-popup-body">
          {!visibleUsers.length ? (
            <div className="aua-empty">No users found in this category.</div>
          ) : (
            <div className="aua-table-wrap">
              <table className="aua-table aua-user-project-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Designation</th>
                    <th>Phone</th>
                    <th>Logins</th>
                    <th>Status</th>
                    <th>Last Login</th>
                  </tr>
                </thead>

                <tbody>
                  {visibleUsers.map((user, index) => (
                    <tr
                      key={
                        user?._id ||
                        user?.id ||
                        user?.emailId ||
                        `${project?.projectId}-${activeTab}-${index}`
                      }
                    >
                      <td>
                        <div className="aua-user-cell">
                          <strong>{getUserName(user)}</strong>
                          <span>{getUserEmail(user)}</span>
                        </div>
                      </td>

                      <td>{getUserRole(user)}</td>
                      <td>{getUserDesignation(user)}</td>
                      <td>{getUserPhone(user)}</td>
                      <td>{user?.loginCount ?? user?.count ?? 0}</td>

                      <td>
                        <span
                          className={
                            user?.isActive === false
                              ? "aua-status-pill inactive"
                              : "aua-status-pill active"
                          }
                        >
                          {user?.isActive === false ? "Inactive" : "Active"}
                        </span>
                      </td>

                      <td>
                        {formatDateTime(
                          user?.lastLogin ||
                            user?.lastLoginAt ||
                            user?.updatedAt,
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminUserAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await rohanApiClient.get(API_URL);
      setAnalytics(response?.data?.data || null);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load analytics",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const overview = analytics?.overview || {};

  const projectUsage = analytics?.projectUsage || [];
  const topUsers = useMemo(
    () => (analytics?.topUsers || []).filter((user) => !isAdminUser(user)),
    [analytics],
  );

  const recentLogins = useMemo(
    () => (analytics?.recentLogins || []).filter((user) => !isAdminUser(user)),
    [analytics],
  );
  const userGrowth = analytics?.userGrowth || [];

  const roleDistribution = useMemo(() => {
    const roles = analytics?.roleDistribution || [];

    return roles.filter((item) => {
      const role =
        typeof item?._id === "object"
          ? item?._id?.roleName ||
            item?._id?.name ||
            item?._id?.title ||
            item?._id?.label ||
            item?._id?.value
          : item?._id;

      const normalizedRole = String(role || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "");

      return !["admin", "administrator", "superadmin"].includes(normalizedRole);
    });
  }, [analytics]);

  const projectUsers = useMemo(() => {
    const rawProjects =
      analytics?.projectUsers ||
      analytics?.projectWiseUsers ||
      analytics?.projectsWithUsers ||
      [];

    if (Array.isArray(rawProjects) && rawProjects.length) {
      return rawProjects.map((project, index) => {
        const users = Array.isArray(project?.users)
          ? project.users.filter((user) => !isAdminUser(user))
          : [];
        const projectId =
          project?.projectId ||
          project?.project?._id ||
          project?.project?.id ||
          project?._id ||
          project?.id ||
          `project-${index}`;

        const projectName =
          project?.projectName ||
          project?.project?.name ||
          project?.project?.projectName ||
          project?.name ||
          "Unnamed Project";

        const activeUsers = users.filter(
          (user) => user?.isActive !== false,
        ).length;

        const inactiveUsers = users.filter(
          (user) => user?.isActive === false,
        ).length;
        return {
          projectId,
          projectName,
          totalUsers: users.length,
          activeUsers,
          inactiveUsers,
          users,
        };
      });
    }

    /*
      Fallback:
      This only works if projectUsage already contains users[] inside each project.
      Otherwise backend must add projectUsers[].
    */
    return projectUsage.map((project, index) => {
      const users = Array.isArray(project?.users) ? project.users : [];

      const projectId =
        project?.projectId ||
        project?.project?._id ||
        project?.project?.id ||
        project?._id ||
        project?.id ||
        `project-usage-${index}`;

      const projectName =
        project?.projectName ||
        project?.project?.name ||
        project?.project?.projectName ||
        project?.name ||
        project?._id ||
        "Unnamed Project";

      return {
        projectId,
        projectName,
        totalUsers: project?.totalUsers ?? project?.count ?? users.length,
        activeUsers:
          project?.activeUsers ??
          users.filter((user) => user?.isActive !== false).length,
        inactiveUsers:
          project?.inactiveUsers ??
          users.filter((user) => user?.isActive === false).length,
        users,
      };
    });
  }, [analytics, projectUsage]);

  return (
    <div className="aua-page">
      {loading ? (
        <div className="aua-state-card">Loading analytics...</div>
      ) : error ? (
        <div className="aua-state-card aua-error">
          <div>{error}</div>

          <button className="aua-retry-btn" onClick={fetchAnalytics}>
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="aua-stats-grid">
            <StatCard label="Total Users" value={overview.totalUsers} />
            <StatCard label="Active Today" value={overview.activeToday} />
            <StatCard label="Active This Week" value={overview.activeWeek} />
            <StatCard label="Active This Month" value={overview.activeMonth} />
            <StatCard label="Inactive Users" value={overview.inactiveUsers} />
          </div>

          <div className="aua-layout-grid">
            <BarList
              title="Role Distribution"
              data={roleDistribution}
              valueKey="count"
              labelKey="_id"
              emptyText="No role distribution found"
            />

            <BarList
              title="Project Usage"
              data={projectUsage}
              valueKey="userCount"
              labelKey="projectName"
              emptyText="No project usage found"
            />

            <GrowthChart data={userGrowth} />

            <SimpleTable
              title="Top Users"
              rows={topUsers}
              emptyText="No top users found"
              columns={[
                {
                  key: "name",
                  label: "User",
                  render: (row) => (
                    <div className="aua-user-cell">
                      <strong>{getUserName(row)}</strong>
                      <span>{getUserEmail(row)}</span>
                    </div>
                  ),
                },
                {
                  key: "role",
                  label: "Role",
                  render: (row) => getUserRole(row),
                },
                {
                  key: "loginCount",
                  label: "Logins",
                  render: (row) => row?.loginCount ?? row?.count ?? 0,
                },
                {
                  key: "lastLogin",
                  label: "Last Login",
                  render: (row) =>
                    formatDateTime(row?.lastLogin || row?.lastLoginAt),
                },
              ]}
            />

            <SimpleTable
              title="Recent Logins"
              rows={recentLogins}
              emptyText="No recent logins found"
              columns={[
                {
                  key: "name",
                  label: "User",
                  render: (row) => (
                    <div className="aua-user-cell">
                      <strong>{getUserName(row)}</strong>
                      <span>{getUserEmail(row)}</span>
                    </div>
                  ),
                },
                {
                  key: "role",
                  label: "Role",
                  render: (row) => getUserRole(row),
                },
                {
                  key: "lastLogin",
                  label: "Login Time",
                  render: (row) =>
                    formatDateTime(row?.lastLogin || row?.lastLoginAt),
                },
              ]}
            />
          </div>

          <ProjectUsersPanel projectUsers={projectUsers} />
        </>
      )}
    </div>
  );
};

export default AdminUserAnalytics;
